import React from 'react';
import type { ComponentConfig, CustomField } from '@puckeditor/core';
import styled from 'styled-components';

import { createInheritColorRoleField } from '../fields/widgets/inherit-color-role-field';
import { SURFACE_COLOR_FIELD_AI_SCHEMA, InheritSurfaceFillField } from '../fields/widgets/surface-color-field';
import {
  BACKGROUND_OPACITY_FIELD,
  DEFAULT_BACKGROUND_OPACITY,
  resolveBackgroundColorWithOpacity,
  resolveBackgroundOpacityFields,
} from '../fields/background-opacity';
import { customTextSizeField, TEXT_WEIGHT_FIELD, customTextSizeCss, textWeightCss } from '../fields/text-appearance';
import type { TextWeight } from '../fields/text-appearance';
import { toggleField } from '../fields/widgets/toggle-field';
import { renderFieldGroups } from '../fields/field-groups';
import type { FieldGroupDefinition } from '../fields/field-groups';
import { createFlatSpacingFields, readBoxSpacing } from '../fields/spacing-fields';
import type { FlatSpacingProps } from '../fields/spacing-fields';
import { CmsHeadingRichTextMenu, CmsRichTextMenu, cmsRichTextSelector } from '../fields/widgets/richtext-menu';
import { interpolateHtml } from '../interpolate-html';
import { getCmsVariables } from '../interpolation';
import { headingDocument } from './heading-document';
import { normalizeHeadingEditorHtml, semanticHeadingHtml } from './heading-html';
import {
  BLOCK_WIDTHS,
  FONT_ROLES,
  HEADING_LINE_HEIGHT,
  HEADING_LINE_HEIGHTS,
  HEADING_PRESETS,
  lookup,
  MAX_WIDTHS,
  NO_SPACING,
  resolveTextColor,
  spacingToCss,
  SURFACE_COLOR_ROLES,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type { BlockWidthOption, FontRole, HeadingLineHeight } from '../fields/tokens';

export const DEFAULT_HEADING_HTML = '<p>Heading</p>';

const HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
/** Inherit means the default text color; saved data is unchanged. */
const INHERIT_TEXT_COLOR_FIELD = createInheritColorRoleField('text');

type HeadingLevel = typeof HEADING_LEVELS[number];
/** `level` = the default size of the selected level; `custom` = the author's own pixel size. */
const LEVEL_PRESET = 'level';
type HeadingSize = typeof LEVEL_PRESET | 'custom';

export type HeadingProps = FlatSpacingProps<'padding'> & {
  text: string;
  /** Semantic tag and, unless a custom size is on, its default visual size. */
  level: HeadingLevel;
  /** `level` (default) follows the selected level's default size; `custom` uses `customSize`. */
  preset: HeadingSize;
  /** Pixel size used when `preset` is `custom`. */
  customSize?: number;
  fontWeight?: TextWeight;
  lineHeight: HeadingLineHeight;
  font: FontRole;
  color: string;
  /** Either a surface role key (e.g. `'surface'`) or a custom hex (e.g. `'#ff0000'`). */
  background: string;
  backgroundOpacity: number;
  width: BlockWidthOption;
};

const HEADING_FIELD_GROUPS: readonly FieldGroupDefinition<keyof HeadingProps>[] = [
  {
    label: 'Appearance',
    fields: [
      'level',
      'preset',
      'customSize',
      'fontWeight',
      'lineHeight',
      'font',
      'color',
      'background',
      'backgroundOpacity',
    ],
  },
  { label: 'Layout', fields: ['width'] },
  {
    label: 'Spacing',
    fields: [
      { label: 'Padding', variant: 'card', fields: ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] },
    ],
  },
];

/**
 * The frame owns box styling while its one child owns semantics. In the editor that child is
 * Puck's live Tiptap node; in production it is the canonical semantic heading HTML above.
 */
const StyledHeading = styled.div`
  margin: 0;
  background: var(--cms-heading-background, ${SURFACE_COLOR_ROLES.none});
  color: var(--cms-heading-color, ${TEXT_COLOR_ROLES.text});
  font-family: var(--cms-heading-font-family, ${FONT_ROLES.heading});
  font-size: var(--cms-heading-font-size, ${HEADING_PRESETS.h2.fontSize});
  font-weight: var(--cms-heading-font-weight, ${HEADING_PRESETS.h2.fontWeight});
  line-height: var(--cms-heading-line-height, ${HEADING_LINE_HEIGHT});
  width: var(--cms-heading-width, ${BLOCK_WIDTHS.fill});
  max-width: var(--cms-heading-max-width, ${MAX_WIDTHS.none});
  padding: var(--cms-heading-padding, 0px);

  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: inherit;
    font: inherit;
    margin: 0;
  }

  a {
    color: var(--dcm-cms-heading-link-color, var(--dcm-color-link, currentColor));
    text-decoration: underline;
  }
`;

const PRESET_VALUES: HeadingSize[] = [LEVEL_PRESET, 'custom'];

const customSizeToggle = toggleField(
  'Custom text size',
  "Turn on to set your own pixel size; leave off to use the default size of the selected `level`. Store 'custom' when on and 'level' when off.",
  'Off: the level sets the size. On: your size overrides it.'
);
const ToggleWidget = customSizeToggle.render;

/** Presents `preset` as an on/off switch: on = `custom`, off = the level's own size. */
const CustomTextSizeField: CustomField<HeadingSize> = {
  type: 'custom',
  label: 'Custom text size',
  render: function CustomTextSizeSwitch({ value, onChange, field, ...rest }) {
    return (
      <ToggleWidget
        {...rest}
        field={(field as unknown) as Parameters<typeof ToggleWidget>[0]['field']}
        value={value === 'custom'}
        onChange={(on) => onChange(on ? 'custom' : LEVEL_PRESET)}
      />
    );
  },
  ai: {
    schema: { type: 'string', enum: PRESET_VALUES },
    instructions:
      "Leave as 'level' so the heading takes the default size of its `level`; use 'custom' (with `customSize`) only when the design requires a specific pixel size.",
  },
};

const LINE_HEIGHT_OPTIONS: { label: string; value: HeadingLineHeight }[] = [
  { label: 'System default', value: 'system' },
  { label: 'Tight', value: 'tight' },
  { label: 'Comfortable', value: 'comfortable' },
  { label: 'Spacious', value: 'spacious' },
];

export const headingConfig: ComponentConfig<HeadingProps> = {
  label: 'Heading',
  ai: {
    instructions:
      'A rich-text heading for page titles and content hierarchy. Set `level` (h1-h6) for both document structure (one h1 per page) and the default visual size. Supports inline formatting, alignment, links, and CMS variable tokens.',
  },
  fields: {
    text: {
      type: 'richtext',
      label: 'Heading content',
      contentEditable: true,
      renderMenu: CmsHeadingRichTextMenu,
      renderInlineMenu: CmsRichTextMenu,
      tiptap: { extensions: [headingDocument], selector: cmsRichTextSelector },
      options: {
        document: false,
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        listItem: false,
        bulletList: false,
        orderedList: false,
        listKeymap: false,
        link: { openOnClick: false },
      },
    },
    level: {
      type: 'select',
      label: 'Level',
      options: HEADING_LEVELS.map((level) => ({ label: level.toUpperCase(), value: level })),
      ai: {
        schema: { type: 'string', enum: [...HEADING_LEVELS] },
        instructions:
          'Heading level, which sets both the HTML heading level (accessibility and SEO) and the default visual size. Use h1 for the page title (once per page), h2 for sections, h3+ for subsections.',
      },
    },
    preset: CustomTextSizeField,
    customSize: customTextSizeField(32),
    fontWeight: TEXT_WEIGHT_FIELD,
    lineHeight: {
      type: 'select',
      label: 'Line height',
      options: LINE_HEIGHT_OPTIONS,
      ai: {
        schema: { type: 'string', enum: LINE_HEIGHT_OPTIONS.map(({ value }) => value) },
        instructions:
          "Line height multiplier for the heading. Leave as 'system' to inherit the theme default; choose tight, comfortable, or spacious when visual rhythm requires it.",
      },
    },
    font: {
      type: 'radio',
      label: 'Font',
      options: [
        { label: 'Heading', value: 'heading' },
        { label: 'Body', value: 'body' },
      ],
      ai: {
        schema: { type: 'string', enum: ['heading', 'body'] },
        instructions:
          "Which of the merchant's two configured fonts to use. Prefer 'heading' unless the text is deliberately styled as body copy.",
      },
    },
    color: {
      type: 'custom',
      label: 'Text color',
      render: INHERIT_TEXT_COLOR_FIELD,
      ai: {
        schema: {
          type: 'string',
          description:
            "Either a theme color role ('text', 'secondary', 'muted', 'inverted', 'link') or a hex value such as #121516. Prefer a role so the heading follows merchant branding.",
        },
      },
    },
    background: {
      type: 'custom',
      label: 'Background',
      render: InheritSurfaceFillField,
      ai: {
        schema: SURFACE_COLOR_FIELD_AI_SCHEMA,
        instructions:
          "Optional fill behind the heading. Set to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand color is required.",
      },
    },
    backgroundOpacity: BACKGROUND_OPACITY_FIELD,
    width: {
      type: 'radio',
      label: 'Width',
      options: [
        { label: 'Fit', value: 'fit' },
        { label: 'Fill', value: 'fill' },
      ],
      ai: { schema: { type: 'string', enum: ['fit', 'fill'] } },
    },
    ...createFlatSpacingFields('padding'),
  },
  renderFields: ({ fields }) => (
    <>
      {fields.text}
      {renderFieldGroups(fields, HEADING_FIELD_GROUPS)}
    </>
  ),
  resolveFields: (data, { fields }) => {
    const { customSize, ...baseFields } = resolveBackgroundOpacityFields(data.props.background, fields);
    return Object.fromEntries(
      Object.entries(baseFields).flatMap(([key, field]) =>
        key === 'preset' && data.props.preset === 'custom'
          ? [
              [key, field],
              ['customSize', customSize],
            ]
          : [[key, field]]
      )
    );
  },
  resolveData: ({ props }) => {
    const normalizedText = normalizeHeadingEditorHtml(props.text);
    return {
      props: {
        ...props,
        text: normalizedText,
        ...(props.preset === 'custom' && props.customSize === undefined ? { customSize: 32 } : {}),
      },
    };
  },
  defaultProps: {
    text: DEFAULT_HEADING_HTML,
    level: 'h2',
    preset: LEVEL_PRESET,
    customSize: 32,
    fontWeight: 'system',
    lineHeight: 'system',
    font: 'heading',
    color: 'text',
    background: 'none',
    backgroundOpacity: DEFAULT_BACKGROUND_OPACITY,
    width: 'fill',
    paddingTop: NO_SPACING.top,
    paddingRight: NO_SPACING.right,
    paddingBottom: NO_SPACING.bottom,
    paddingLeft: NO_SPACING.left,
  },
  render: ({
    text = DEFAULT_HEADING_HTML,
    level = 'h2',
    preset = LEVEL_PRESET,
    customSize = 32,
    fontWeight = 'system',
    lineHeight = 'system',
    font = 'heading',
    color = 'text',
    background = 'none',
    backgroundOpacity = DEFAULT_BACKGROUND_OPACITY,
    width = 'fill',
    puck,
    ...spacingProps
  }) => {
    const content = text as unknown;
    const tag = HEADING_LEVELS.includes(level) ? level : 'h2';
    // Size follows the level except in custom mode, which overrides only the size: the level's
    // weight (H3-H6 are lighter than H2) always applies.
    const sizing = lookup(HEADING_PRESETS, tag, HEADING_PRESETS.h2);
    const resolvedLineHeight =
      lineHeight === 'system' ? undefined : lookup(HEADING_LINE_HEIGHTS, lineHeight, undefined);
    const resolvedWeight = textWeightCss(fontWeight);
    const style = {
      '--cms-heading-background': resolveBackgroundColorWithOpacity(background, backgroundOpacity),
      '--cms-heading-color': resolveTextColor(color),
      '--cms-heading-font-family': lookup(FONT_ROLES, font, FONT_ROLES.heading),
      '--cms-heading-font-size': preset === 'custom' ? customTextSizeCss(customSize, 32) : sizing.fontSize,
      '--cms-heading-font-weight': sizing.fontWeight,
      ...(resolvedWeight ? { '--cms-heading-font-weight': resolvedWeight } : {}),
      ...(resolvedLineHeight
        ? {
            '--cms-heading-line-height': resolvedLineHeight,
          }
        : {}),
      '--cms-heading-width': lookup(BLOCK_WIDTHS, width, BLOCK_WIDTHS.fill),
      '--cms-heading-max-width': MAX_WIDTHS.none,
      '--cms-heading-padding': spacingToCss(readBoxSpacing(spacingProps, 'padding')),
    } as React.CSSProperties;

    if (typeof content !== 'string') {
      return puck.isEditing ? <StyledHeading style={style}>{content as React.ReactNode}</StyledHeading> : <></>;
    }

    const interpolatedHtml = interpolateHtml(content, getCmsVariables(puck));
    const renderedHtml = semanticHeadingHtml(interpolatedHtml, tag);
    return renderedHtml ? <StyledHeading style={style} dangerouslySetInnerHTML={{ __html: renderedHtml }} /> : <></>;
  },
};
